'use client';

import { Category } from '@/lib/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BookOpen, MoreHorizontal, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import * as Icons from 'lucide-react';

interface CategoryCardProps {
  category: Category;
  onView?: (category: Category) => void;
  onEdit?: (category: Category) => void;
}

export function CategoryCard({ category, onView, onEdit }: CategoryCardProps) {
  const IconComponent = (Icons as Record<string, React.ComponentType<{ className?: string }>>)[category.icon] || Icons.BookOpen;

  return (
    <div 
      className="group relative p-6 rounded-xl bg-[#112240] border border-[#1d3557] card-hover overflow-hidden cursor-pointer"
      onClick={() => onView?.(category)}
    >
      {/* Background accent */}
      <div 
        className="absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-10 -mr-16 -mt-16 transition-opacity group-hover:opacity-20"
        style={{ backgroundColor: category.color }}
      />
      
      <div className="relative z-10">
        <div className="flex items-start justify-between">
          <div 
            className="w-14 h-14 rounded-xl flex items-center justify-center"
            style={{ 
              backgroundColor: `${category.color}20`,
              color: category.color 
            }}
          >
            <IconComponent className="w-7 h-7" />
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="text-[#8892B0] hover:text-[#E6F1FF] hover:bg-[#1d3557]"
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.(category);
            }}
          >
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </div>

        <div className="mt-4">
          <h3 className="text-lg font-semibold text-[#E6F1FF] group-hover:text-[#64FFDA] transition-colors">
            {category.name}
          </h3>
          <p className="text-sm text-[#8892B0] mt-1 line-clamp-2">
            {category.description}
          </p>
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#1d3557]">
          <div className="flex items-center gap-2 text-sm text-[#8892B0]">
            <BookOpen className="w-4 h-4" />
            <span>{category.bookCount} 本图书</span>
          </div>
          <ArrowRight className="w-4 h-4 text-[#8892B0] group-hover:text-[#64FFDA] group-hover:translate-x-1 transition-all" />
        </div>
      </div>
    </div>
  );
}
